import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ManagementAssessmentForm from '../components/management/ManagementAssessmentForm';
import PatientSheet from '../components/management/PatientSheet';
import DoutoFeatureCard from '../components/DoutoFeatureCard';
import DoutoReengagementPopup from '../components/onboarding/DoutoReengagementPopup';
import OnboardingFlow from '../components/onboarding/OnboardingFlow';
import SafeIcon from '../common/SafeIcon';
import { useLocale } from '../components/LocaleProvider';

function formatDate(value, es = false) {
  if (!value) return es ? 'Todavía no' : 'Ainda não';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : (es ? 'Todavía no' : 'Ainda não');
}

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

function Modal({ title, children, onClose, labelledBy }) {
  const { locale } = useLocale();
  const es = locale === 'es';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(15,39,48,0.5)] px-4 py-5" role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
      <button type="button" aria-label={es ? 'Cerrar ventana' : 'Fechar janela'} onClick={onClose} className="absolute inset-0 cursor-default" />
      <div className="sheet raised relative z-10 max-h-[92vh] w-full max-w-xl overflow-y-auto p-5 md:p-7">
        <div className="mb-5 flex items-start justify-between gap-4 border-b border-[var(--border)] pb-4">
          <h2 id={labelledBy} className="text-xl font-semibold text-[var(--primary)]">{title}</h2>
          <button type="button" onClick={onClose} aria-label={es ? 'Cerrar ventana' : 'Fechar janela'} className="flex h-11 w-11 shrink-0 items-center justify-center text-[var(--primary)] hover:bg-[var(--surface-soft)]"><SafeIcon name="X" className="h-6 w-6" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Gestao() {
  const { locale } = useLocale();
  const spanish = locale === 'es';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchHint, setSearchHint] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [assessmentPatient, setAssessmentPatient] = useState(undefined);
  const [patientName, setPatientName] = useState('');
  const [patientBirthDate, setPatientBirthDate] = useState('');
  const [savingPatient, setSavingPatient] = useState(false);
  const [showDoutoPopup, setShowDoutoPopup] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  // Cobre quem já tinha sessão aberta antes do onboarding existir, ou entrou direto
  // em /dashboard sem passar pelo login agora. Roda em paralelo ao carregamento da
  // ficha, mas o onboarding, se precisar aparecer, aparece antes dela.
  useEffect(() => {
    fetch('/api/auth/onboarding-status', { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((payload) => { if (payload.showOnboarding) setShowOnboarding(true); })
      .catch(() => {})
      .finally(() => setOnboardingChecked(true));
  }, []);

  // Popup de reengajamento: só quando o login (functions/lib/engagement-state.js) marcou
  // o retorno após 3+ dias. Some da URL logo em seguida para um refresh não repetir.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('douto') === '1') {
      setShowDoutoPopup(true);
      params.delete('douto');
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
      window.history.replaceState(null, '', next);
    }
  }, []);

  useEffect(() => {
    const value = searchInput.trim();
    if (value && value.length < 3) {
      setSearchHint(spanish ? 'Escribe al menos 3 caracteres para buscar.' : 'Digite pelo menos 3 caracteres para pesquisar.');
      return undefined;
    }
    setSearchHint('');
    const timer = window.setTimeout(() => {
      setPage(1);
      setSearch(value);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ page: String(page), limit: '10' });
    if (search) params.set('search', search);
    setLoading(true);
    setError('');
    fetch(`/api/management/summary?${params.toString()}`, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (response.status === 401) {
          navigate('/pro?access=login', { replace: true });
          return;
        }
        if (!response.ok) throw new Error(spanish ? 'No fue posible cargar el panel.' : (payload.error || 'Não foi possível carregar sua gestão.'));
        window.eidocAnalytics?.capture('dashboard_viewed', { patient_count: payload?.patient_pagination?.total || payload?.patients?.length || 0, page, has_search: Boolean(search) });
        setData(payload);
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [navigate, page, search, reloadKey]);

  const recentActivity = data?.recent_activity || [];
  const latestActivity = recentActivity[0];
  const visiblePatients = data?.patients || [];
  const visibleAssessmentCount = visiblePatients.reduce((total, patient) => total + (patient.assessment_count || 0), 0);

  function refreshSummary() {
    setReloadKey((value) => value + 1);
  }

  function closePatientForm() {
    if (savingPatient) return;
    setShowPatientForm(false);
    setPatientName('');
    setPatientBirthDate('');
  }

  async function addPatient(event) {
    event.preventDefault();
    setSavingPatient(true);
    setError('');
    try {
      const response = await fetch('/api/management/summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ display_name: patientName, birth_date: patientBirthDate || null }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(spanish ? 'No fue posible guardar los datos de la persona.' : (payload.error || 'Não foi possível salvar a pessoa.'));
      window.eidocAnalytics?.capture('patient_created', { has_birth_date: Boolean(patientBirthDate) });
      closePatientForm();
      refreshSummary();
      setSelectedPatient(payload.patient);
    } catch (requestError) {
      setError(spanish ? 'No fue posible guardar los datos de la persona.' : (requestError.message || 'Não foi possível salvar a pessoa.'));
    } finally {
      setSavingPatient(false);
    }
  }

  function handlePatientChanged(patient) {
    if (patient?.id) setSelectedPatient((current) => ({ ...current, ...patient }));
    refreshSummary();
  }
 
  if (showOnboarding) {
    return <OnboardingFlow onDone={() => setShowOnboarding(false)} />;
  }

  if (!onboardingChecked || (loading && !data)) {
    return <main className="clinical-workspace px-0 py-4 md:px-4 md:py-8"><section className="mx-auto min-h-[80vh] max-w-[1120px] px-4 py-16"><p className="text-[var(--text-muted)]">{spanish ? 'Cargando tu panel…' : 'Carregando sua gestão…'}</p></section></main>;
  }

  if (error && !data) {
    return (
      <main className="clinical-workspace mx-auto max-w-[680px] px-4 py-16">
        <div className="sheet p-6">
          <h1 className="text-[var(--primary)]">{spanish ? 'No se pudo cargar el panel' : 'Seu dashboard não carregou'}</h1>
          <p className="mt-3 text-[var(--text-muted)]">{error}</p>
          <button type="button" onClick={refreshSummary} className="btn-primary mt-6">{spanish ? 'Intentar de nuevo' : 'Tentar novamente'}</button>
        </div>
      </main>
    );
  }

  const nameById = new Map(visiblePatients.map((patient) => [String(patient.id), patient.display_name]));

  return (
    <main className="clinical-workspace px-0 py-4 md:px-4 md:py-8">
      <section className="mx-auto max-w-[1120px] px-4 py-6 md:py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[var(--hairline)] pb-5">
          <div>
          <h1>{spanish ? 'Panel clínico' : 'Dashboard clínico'}</h1>
          <p className="mt-1 text-[var(--text-muted)]">{spanish ? 'Personas, evaluaciones y evolución en un solo lugar.' : 'Pessoas, avaliações e evolução em um só lugar.'}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/gestao" className="btn-secondary no-underline">{spanish ? 'Mi cuenta' : 'Minha conta'}</Link>
            <button type="button" onClick={() => { setNotice(''); window.eidocAnalytics?.capture('assessment_form_opened', { surface: 'dashboard' }); setAssessmentPatient(null); }} className="btn-secondary">{spanish ? 'Nueva evaluación' : 'Nova avaliação'}</button>
            <button type="button" onClick={() => { window.eidocAnalytics?.capture('patient_form_opened', { surface: 'dashboard' }); setShowPatientForm(true); }} className="btn-primary">{spanish ? 'Agregar persona' : 'Adicionar pessoa'}</button>
          </div>
        </div>

        {error && data && <p role="alert" className="mt-4 border-l-4 border-[var(--danger-text)] bg-[var(--danger-bg)] px-4 py-3 text-sm font-semibold text-[var(--danger-text)]">{error}</p>}
        {data?.warning && <p role="status" className="mt-4 border-l-4 border-[var(--warning-text)] bg-[var(--warning-bg)] px-4 py-3 text-sm font-semibold text-[var(--warning-text)]">{data.warning}</p>}
        {notice && <p role="status" className="mt-4 border-l-4 border-[var(--warning-text)] bg-[var(--warning-bg)] px-4 py-3 text-sm font-semibold text-[var(--warning-text)]">{notice}</p>}

        <dl className="mt-5 grid gap-3 sm:grid-cols-3">
          {[
            [spanish ? 'Personas atendidas' : 'Pessoas acompanhadas', data?.patient_pagination?.total ?? visiblePatients.length, 'Users'],
            [spanish ? 'Evaluaciones en esta página' : 'Avaliações nesta página', visibleAssessmentCount, 'FileText'],
            [spanish ? 'Última evaluación' : 'Última avaliação', latestActivity?.applied_at ? formatDate(latestActivity.applied_at, spanish) : formatDate('', spanish), 'Clock'],
          ].map(([label, value, icon]) => (
            <div key={label} className="card flex items-center gap-3 p-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[var(--accent-tint)] text-[var(--accent)]" aria-hidden="true">
                <SafeIcon name={icon} className="h-5 w-5" />
              </span>
              <div>
                <dt className="text-sm text-[var(--text-muted)]">{label}</dt>
                <dd className="score-figure text-2xl text-[var(--primary)]">{value}</dd>
              </div>
            </div>
          ))}
        </dl>

        <section className="mt-8" aria-labelledby="pessoas-titulo">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 id="pessoas-titulo">{spanish ? 'Personas atendidas' : 'Pessoas acompanhadas'}</h2>
              <p className="mt-1 text-sm text-[var(--text-muted)]">{spanish ? 'Busca por nombre. Cada ficha muestra solo el historial MiniMental de esta cuenta.' : 'Pesquise pelo nome. A ficha mostra somente o histórico MiniMental desta conta.'}</p>
            </div>
            <form onSubmit={(event) => { event.preventDefault(); const value = searchInput.trim(); if (value && value.length < 3) { setSearchHint(spanish ? 'Escribe al menos 3 caracteres para buscar.' : 'Digite pelo menos 3 caracteres para pesquisar.'); return; } window.eidocAnalytics?.capture('patient_search_submitted', { has_query: Boolean(value), query_length: value.length }); setPage(1); setSearch(value); }} className="flex w-full gap-2 sm:w-auto">
              <label className="sr-only" htmlFor="patient-search">{spanish ? 'Buscar persona' : 'Pesquisar pessoa'}</label>
              <input id="patient-search" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder={spanish ? 'Buscar por nombre' : 'Pesquisar por nome'} className="input-field min-w-0 sm:w-64" />
              <button type="submit" className="btn-secondary">{spanish ? 'Buscar' : 'Pesquisar'}</button>
            </form>
          </div>
          {searchHint && <p role="status" className="mt-2 text-sm text-[var(--text-muted)]">{searchHint}</p>}

          {visiblePatients.length ? (
            <div className="mt-4 overflow-x-auto border-t border-[var(--border-strong)]">
              <table className="data-table min-w-[620px]">
                <caption className="sr-only">{spanish ? 'Personas atendidas' : 'Pessoas acompanhadas'}</caption>
                <thead>
                  <tr>
                    <th className="py-3">{spanish ? 'Nombre' : 'Nome'}</th>
                    <th>{spanish ? 'Nacimiento' : 'Nascimento'}</th>
                    <th className="text-right">{spanish ? 'Evaluaciones' : 'Avaliações'}</th>
                    <th>{spanish ? 'Última evaluación' : 'Última avaliação'}</th>
                    <th><span className="sr-only">{spanish ? 'Acciones' : 'Ações'}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePatients.map((patient) => (
                    <tr key={patient.id} className="hover:bg-[var(--surface-soft)]">
                      <td className="py-2">
                        <button type="button" onClick={() => { window.eidocAnalytics?.capture('patient_opened', { assessment_count: patient.assessment_count || 0 }); setSelectedPatient(patient); }} className="flex items-center gap-2.5 text-left font-semibold text-[var(--primary)] underline-offset-4 hover:underline">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent-tint)] text-xs font-bold text-[var(--accent)]" aria-hidden="true">{initials(patient.display_name)}</span>
                          {patient.display_name}
                        </button>
                      </td>
                      <td className="text-[var(--text-muted)]">{formatDate(patient.birth_date, spanish)}</td>
                      <td className="text-right">{patient.assessment_count || 0}</td>
                      <td>{patient.latest_assessment ? <><strong className="font-semibold">{patient.latest_assessment.score}/30</strong> <span className="text-[var(--text-muted)]">{spanish ? 'el' : 'em'} {formatDate(patient.latest_assessment.applied_at, spanish)}</span></> : <span className="text-[var(--text-muted)]">—</span>}</td>
                      <td className="whitespace-nowrap text-right">
                        <button type="button" onClick={() => setAssessmentPatient(patient)} className="px-2 text-sm text-[var(--primary)] underline">{spanish ? 'Nueva evaluación' : 'Nova avaliação'}</button>
                        <button type="button" onClick={() => setSelectedPatient(patient)} className="px-2 text-sm text-[var(--primary)] underline">{spanish ? 'Abrir ficha' : 'Abrir ficha'}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-4 border-y border-[var(--border)] py-8 text-center">
              <span className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--accent-tint)] text-[var(--accent)]" aria-hidden="true">
                <SafeIcon name="Users" className="h-5 w-5" />
              </span>
              <p className="text-lg font-bold text-[var(--primary)]">{spanish ? (search ? 'No se encontraron personas.' : 'Empieza aquí el seguimiento de una persona.') : (search ? 'Nenhuma pessoa encontrada.' : 'Sua primeira pessoa acompanhada começa aqui.')}</p>
              <p className="mt-1 text-sm leading-relaxed text-[var(--text-muted)]">{spanish ? (search ? 'Prueba otro nombre o borra la búsqueda.' : 'Registra solo los datos necesarios para empezar a seguir la evolución.') : (search ? 'Tente outro nome ou limpe a pesquisa.' : 'Cadastre apenas o necessário para começar a acompanhar a evolução.')}</p>
              {!search && <button type="button" onClick={() => setShowPatientForm(true)} className="btn-primary mt-4">{spanish ? 'Agregar persona' : 'Adicionar pessoa'}</button>}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[var(--text-muted)]">{data?.patient_pagination?.total || 0} {spanish ? ((data?.patient_pagination?.total || 0) === 1 ? 'persona' : 'personas') : ((data?.patient_pagination?.total || 0) === 1 ? 'pessoa' : 'pessoas')}</p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page === 1 || loading} className="btn-secondary !min-h-10 !py-1 text-sm disabled:opacity-40">{spanish ? 'Anterior' : 'Anterior'}</button>
              <span className="px-2 text-sm text-[var(--text-muted)]">{spanish ? 'Página' : 'Página'} {data?.patient_pagination?.page || page}</span>
              <button type="button" onClick={() => setPage((current) => current + 1)} disabled={!data?.patient_pagination?.has_next || loading} className="btn-secondary !min-h-10 !py-1 text-sm disabled:opacity-40">{spanish ? 'Siguiente' : 'Próxima'}</button>
            </div>
          </div>
        </section>

        <section className="mt-10" aria-labelledby="atividade-titulo">
          <h2 id="atividade-titulo">{spanish ? 'Actividad reciente' : 'Atividade recente'}</h2>
          <p className="mt-1 text-sm text-[var(--text-muted)]">{spanish ? 'Los últimos registros MiniMental de las personas de esta página.' : 'Os últimos registros MiniMental dos pacientes desta página.'}</p>
          {recentActivity.length ? (
            <table className="data-table mt-4 border-t border-[var(--border-strong)]">
              <caption className="sr-only">{spanish ? 'Actividad reciente' : 'Atividade recente'}</caption>
              <thead><tr><th className="py-3">{spanish ? 'Fecha' : 'Data'}</th><th>{spanish ? 'Persona' : 'Pessoa'}</th><th className="text-right">{spanish ? 'Puntuación' : 'Pontuação'}</th></tr></thead>
              <tbody>
                {recentActivity.map((assessment) => (
                  <tr key={assessment.id}>
                    <td className="py-2 tabular">{formatDate(assessment.applied_at, spanish)}</td>
                    <td>{nameById.get(String(assessment.patient_id)) || (spanish ? 'Evaluación registrada' : 'Avaliação registrada')}</td>
                    <td className="text-right font-semibold tabular">{assessment.score} / 30</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="mt-3 text-[var(--text-muted)]">{spanish ? 'Todavía no hay evaluaciones registradas.' : 'Ainda não há avaliações registradas.'}</p>}
        </section>

        <div className="mt-10">
          <DoutoFeatureCard placement="dashboard" />
        </div>
      </section>

      {showPatientForm && (
        <Modal title={spanish ? 'Agregar persona' : 'Adicionar pessoa'} labelledBy="patient-create-title" onClose={closePatientForm}>
          <form onSubmit={addPatient}>
            <p className="text-sm text-[var(--text-muted)]">{spanish ? 'Usa solo los datos necesarios para organizar el seguimiento.' : 'Use apenas os dados necessários para organizar o acompanhamento.'}</p>
            <label className="mt-5 block text-sm font-bold text-[var(--primary)]">{spanish ? 'Nombre' : 'Nome'}
              <input required minLength={2} maxLength={160} autoFocus value={patientName} onChange={(event) => setPatientName(event.target.value)} className="input-field mt-2" placeholder={spanish ? 'Nombre para identificar' : 'Nome para identificação'} />
            </label>
            <label className="mt-4 block text-sm font-bold text-[var(--primary)]">{spanish ? 'Fecha de nacimiento' : 'Data de nascimento'}
              <input type="date" value={patientBirthDate} onChange={(event) => setPatientBirthDate(event.target.value)} className="input-field mt-2" />
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={closePatientForm} disabled={savingPatient} className="btn-secondary">{spanish ? 'Cancelar' : 'Cancelar'}</button>
              <button type="submit" disabled={savingPatient} className="btn-primary">{savingPatient ? (spanish ? 'Guardando…' : 'Salvando…') : (spanish ? 'Guardar persona' : 'Salvar pessoa')}</button>
            </div>
          </form>
        </Modal>
      )}

      {assessmentPatient !== undefined && assessmentPatient !== null && (
        <Modal title={spanish ? 'Nueva evaluación' : 'Nova avaliação'} labelledBy="management-assessment-modal-title" onClose={() => setAssessmentPatient(undefined)}>
          <ManagementAssessmentForm
            patients={[assessmentPatient]}
            initialPatientId={assessmentPatient.id}
            onSaved={(result) => { setAssessmentPatient(undefined); refreshSummary(); if (result?.audit_warning) setNotice(spanish ? 'La evaluación se guardó, pero no pudimos registrar la actividad. Ya está en el historial; no la guardes otra vez.' : 'A avaliação foi salva, mas não conseguimos registrar a atividade. Ela já está no histórico; não tente salvá-la novamente.'); }}
            onCancel={() => setAssessmentPatient(undefined)}
          />
        </Modal>
      )}

      {assessmentPatient === null && (
        <Modal title={spanish ? 'Nueva evaluación' : 'Nova avaliação'} labelledBy="management-assessment-modal-title" onClose={() => setAssessmentPatient(undefined)}>
          <ManagementAssessmentForm
            patients={visiblePatients}
            onSaved={(result) => { setAssessmentPatient(undefined); refreshSummary(); if (result?.audit_warning) setNotice(spanish ? 'La evaluación se guardó, pero no pudimos registrar la actividad. Ya está en el historial; no la guardes otra vez.' : 'A avaliação foi salva, mas não conseguimos registrar a atividade. Ela já está no histórico; não tente salvá-la novamente.'); }}
            onCancel={() => setAssessmentPatient(undefined)}
          />
        </Modal>
      )}

      {selectedPatient && (
        <PatientSheet
          patient={selectedPatient}
          onClose={() => setSelectedPatient(null)}
          onPatientChanged={handlePatientChanged}
        />
      )}

      {showDoutoPopup && <DoutoReengagementPopup onClose={() => setShowDoutoPopup(false)} />}
    </main>
  );
}
